import { FC } from "react";

import { BarEvent, EventsBar } from "@nens/chakra/components";
import { TimeseriesRainOverview } from "@nens/threedi/openapi-client";

import { GridCard } from "../../../components/GridCard";

function eventValuesToBlocks(
  values: number[][],
  duration: number | undefined,
  offset: number
): (BarEvent & { value: number })[] {
  return values
    .map(([start, value], idx): BarEvent & { value: number } => {
      // Let each event run until the timestamp of the next change, or
      // until 'duration'.
      const realStart = start + offset;
      const end =
        idx + 1 < values.length
          ? values[idx + 1][0] + offset
          : offset + (duration ?? values[idx][0]);
      return { start: realStart, end, value };
    })
    .filter(
      // Filter out empty events (especially if duraction equals
      // timestamp of the last event, or is undefined)
      // Also filter out events without rain.
      (event) => event.start !== event.end && event.value > 0
    );
}

function rainToHuman(value: number, unit: string) {
  if (unit === "m/s") {
    return `${((value * 1000 * 3600 * 100) / 100).toFixed(2)} mm / hr`;
  } else {
    return `${value} ${unit}`;
  }
}

interface TimeseriesRainEventProps {
  simulationDuration: number;
  events?: TimeseriesRainOverview[];
}

export const TimeseriesRainEvent: FC<TimeseriesRainEventProps> = ({
  simulationDuration,
  events,
}) => {
  if (!events || !events.length) return null;

  const barEvents: BarEvent[][] = events.map((timeseriesRainEvent) => {
    return eventValuesToBlocks(
      timeseriesRainEvent.values,
      timeseriesRainEvent.duration || undefined,
      timeseriesRainEvent.offset
    ).map((block) => {
      return {
        start: block.start,
        end: block.end,
        title: timeseriesRainEvent.constant ? "Constant rain" : "Rain",
        content: "Amount: " + rainToHuman(block.value, timeseriesRainEvent.units),
        value: Math.max(block.value, 0),
      };
    });
  });

  return (
    <GridCard variant="twocolumns" title="Timeseries rain">
      <EventsBar
        start={0}
        end={simulationDuration}
        events={barEvents.flat()}
        height="30px"
        blockColor="blue.400"
        borderColor="brand.grey.600"
      />
    </GridCard>
  );
};
